import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { showErrorToast } from '../../utils/errorHandler';
import { Car, ArrowLeft, Eye, EyeOff } from 'lucide-react';

const DriverLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.phone || !formData.password) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    // Validate phone format (9 digits without prefix)
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (cleanPhone.length !== 9) {
      toast({
        title: 'Invalid Phone Number',
        description: 'Please enter a valid 9-digit phone number',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.post('/auth/login', {
        phone: `+260${cleanPhone}`,
        password: formData.password,
      });

      const { token, user } = response.data;

      // Store token and user data
      localStorage.setItem('tekstar_token', token);
      localStorage.setItem('tekstar_user', JSON.stringify(user));

      toast({
        title: 'Welcome back!',
        description: 'Logged in successfully',
      });

      // Check if user has driver profile
      try {
        const driverResponse = await api.get('/drivers/my-profile');
        
        // If driver profile exists, redirect to driver dashboard
        if (driverResponse.data) {
          navigate('/driver/dashboard');
        } else {
          // No driver profile, redirect to application
          toast({
            title: 'No Driver Profile',
            description: 'Please complete your driver application',
            variant: 'destructive',
          });
          navigate('/driver/apply');
        }
      } catch (error) {
        // If 404, user needs to apply as driver
        if (error.response?.status === 404) {
          toast({
            title: 'Driver Application Required',
            description: 'Please complete your driver application to continue',
          });
          navigate('/driver/apply');
        } else {
          // Other error, still redirect to dashboard
          navigate('/driver/dashboard');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      showErrorToast(toast, error, 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 flex items-center justify-center px-4 pt-20 pb-8">
      <Card className="w-full max-w-md p-8">
        {/* Back to main site link */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-pink-600 mb-6"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to main site
        </Link>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full mb-4">
            <Car className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Driver Portal</h1>
          <p className="text-gray-600">Sign in to start earning with Tekstar</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number *</Label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-muted-foreground text-sm font-medium" data-testid="phone-prefix">+260</span>
              <Input
                id="phone"
                type="tel"
                className="rounded-l-none"
                placeholder="XXX XXX XXX"
                maxLength={9}
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 9) })}
                required
                data-testid="phone-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white py-6 text-lg font-semibold"
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in as Driver'}
          </Button>
        </form>

        <div className="mt-6 space-y-3">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">New driver?</span>
            </div>
          </div>

          <Link to="/driver/apply">
            <Button
              type="button"
              variant="outline"
              className="w-full"
            >
              Apply to Drive
            </Button>
          </Link>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Not a driver?{' '}
            <Link to="/login" className="text-pink-600 hover:text-pink-700 font-medium">
              Sign in as rider
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default DriverLogin;
